/**
 * DeclineApplicationModal — Review-stage Decline affordance (Bill 2026-05-12 Round 3).
 *
 * Calls POST /v3/carts/{cartId}/decline with one of the curated reasons.
 * Differs from "archive" in that it records ResolutionCode + ResolvedBy +
 * ResolvedAt for audit + FinCEN compliance review.
 */
import { useState } from 'react';
import type { CSSProperties } from 'react';
import { Banner, Button, ModalShell } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { ApiError } from '@retail-ops/api-client';
import type { CartDeclineInput, CartDeclineReason } from '@retail-ops/shared-types';

const SG: CSSProperties = { display: 'grid', gap: 12 };
const SLBL: CSSProperties = { display: 'block', fontSize: 12, color: 'var(--ink-700)', marginBottom: 4 };
const SSEL: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  fontSize: 14,
  background: 'var(--surface)',
  color: 'var(--ink-900)',
};
const STX: CSSProperties = { ...SSEL, minHeight: 80, resize: 'vertical', fontFamily: 'inherit' };

export interface DeclineApplicationModalProps {
  cartId: string;
  open: boolean;
  onClose: () => void;
  onDeclined: () => void;
  officerCode?: string;
  /** Reason preselected when the dialog opens (a gate hard stop opens it on Adverse Action). */
  initialReason?: CartDeclineReason;
}

const REASONS: ReadonlyArray<{ value: CartDeclineReason; label: string; hint: string }> = [
  { value: 'Adverse',   label: 'Adverse Action',  hint: 'Bank-initiated denial (CIP failure, OFAC hit, fraud risk). Generates the adverse-action letter.' },
  { value: 'Cancelled', label: 'Cancelled',       hint: 'Bank cancels the application for operational reasons unrelated to the customer.' },
  { value: 'Withdrew',  label: 'Customer Withdrew', hint: 'Customer chose not to proceed. Captures customer-initiated abandonment.' },
];

export function DeclineApplicationModal(props: DeclineApplicationModalProps): JSX.Element | null {
  const { cartId, open, onClose, onDeclined, officerCode, initialReason } = props;
  const client = useApiClient();
  const [reason, setReason] = useState<CartDeclineReason>(initialReason ?? 'Withdrew');
  const [details, setDetails] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!open) return null;

  const selectedHint = REASONS.find(r => r.value === reason)?.hint ?? '';

  const submit = async () => {
    setSaving(true); setErr(null);
    try {
      const body: CartDeclineInput = {
        declineReason: reason,
        ...(details.trim() ? { details: details.trim() } : {}),
        ...(officerCode ? { officerCode } : {}),
      };
      const r = await client.retail.carts.decline(cartId, body);
      if (!r.success) throw new Error(r.error ?? 'decline failed');
      onDeclined();
    } catch (e: unknown) {
      setErr(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      open
      size="md"
      title="Decline Application"
      onClose={saving ? () => {} : onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={() => void submit()} disabled={saving}>
            {saving ? 'Declining…' : `Decline (${reason})`}
          </Button>
        </>
      }
    >
      {err && <Banner tone="err">{err}</Banner>}
      <div style={SG}>
        <div>
          <label htmlFor="decline-reason" style={SLBL}>Reason</label>
          <select
            id="decline-reason"
            aria-label="Decline reason"
            value={reason}
            onChange={(e) => setReason(e.target.value as CartDeclineReason)}
            disabled={saving}
            style={SSEL}
          >
            {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 4 }}>{selectedHint}</div>
        </div>
        <div>
          <label htmlFor="decline-details" style={SLBL}>Details (FSR rationale — audit-visible)</label>
          <textarea
            id="decline-details"
            aria-label="Decline details"
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            disabled={saving}
            placeholder={
              reason === 'Adverse' ? 'e.g. CIP could not be completed — ID expired and no alternate.'
              : reason === 'Cancelled' ? 'e.g. Product no longer offered; customer informed.'
              : 'e.g. Customer decided to bank elsewhere.'
            }
            style={STX}
          />
        </div>
        <Banner tone="info">
          Declining will set the cart resolution to <strong>{reason}</strong>, release any session
          lock, and archive the cart.
          {reason === 'Adverse' && (
            <>
              {' '}The adverse-action letter is generated for you and filed under the customer in
              Documents (bank-generated). Print and mail it from Documents.
            </>
          )}
        </Banner>
      </div>
    </ModalShell>
  );
}
